import angular from 'angular';
import { Paho } from '@/angular/libs/mqtt';
import _ from 'lodash';
import {topicMatchesWildcardPath} from "@/utils/mqtt";

const retryConnectionLimit = 10;
const keepAliveCheckInterval = 1000; // milliseconds
const realtimeCheckInterval = 5000; // milliseconds
const realtimeThreshold = 5; // seconds
const realtimeErrorThreshold = 0.5; // seconds

export const REALTIME_INDICATOR = {
    LOADING: 'loading',
    UNCERTAIN: 'uncertain',
    REALTIME: 'realtime',
    OFFLINE: 'offline',
};

class PathSubscription {
    /** @type {string} */
    identifier;
    /**
     * @private
     * @type {Function}
     */
    _callback;

    constructor(identifier, callback) {
        this.identifier = identifier;
        this.callback = callback;
    }

    /**
     * @returns {Function}
     */
    get callback() {
        return this._callback;
    }

    /**
     * @param {Function} value
     */
    set callback(value) {
        this._callback = value || PathSubscription.noop;
    }
}

class InstallationSubscription {
    /** @type {string} */
    identifier;
    /** @type {string[]} */
    paths = [];

    constructor(identifier) {
        this.identifier = identifier;
    }

    addPath(path) {
        if (!this.hasPath(path)) {
            this.paths.push(path);
        }
    }

    removePath(path) {
        const index = this.paths.indexOf(path);
        if (index >= 0) {
            this.paths.splice(index, 1);
        }
    }

    hasPath(path) {
        return this.paths.includes(path);
    }
}

class MQTTService {
    REALTIME_INDICATOR = REALTIME_INDICATOR;
    /** @type {Map<string, PathSubscription[]>}*/
    subscriptions = new Map();
    /** @type {Map<string, InstallationSubscription>}*/
    activePortals = new Map();
    lastValueByPaths = new Map();
    lastMessageTs = new Map();
    realtimeTries = new Map();
    realtimeTimeouts = new Map();
    realtimeWatchers = new Map();
    subscribedPortals = new Map();
    activeSubscriptions = new Set();
    reconnectTriesLeft = retryConnectionLimit;
    connected = false;
    disconnected = false;
    connecting = false;
    keepAliveInterval = 30;
    lastKeepAliveTriggered = null;
    keepAliveTimeout = null;
    debouncedKeepAlive = null;

    /** @type {Paho.MQTT.Client|null} */
    client = null;

    /* @ngInject */
    constructor(
        $timeout,
        $log,
        $interval,
        $rootScope,
        vrmConfig,
        UserService,
        ApiService
    ) {
        // Injections
        this.$timeout = $timeout;
        this.$log = $log;
        this.$interval = $interval;
        this.$rootScope = $rootScope;
        this.vrmConfig = vrmConfig;
        this.UserService = UserService;
        this.ApiService = ApiService;
        // Data
        this.host = vrmConfig.mqttHost;
        this.portalId = null;
        this.client = null;
        this.initEvents();
        this.debouncedKeepAlive = _.debounce(this.sendKeepAlive.bind(this), 150);
        this.startRealtimeHandlerThrottled = _.throttle(this.startRealtimeHandler.bind(this), 1000);
    }

    logger(str) {
        this.$log.info(`[MqttService] ${this.host} :: ${str}`);
    }

    initEvents() {
        this.$rootScope.$on("logout", this.onLogout.bind(this));
        this.$rootScope.$on("login", this.onLogin.bind(this));
        this.$rootScope.$on("tokenChange", this.onTokenChange.bind(this));
        this.$rootScope.$on("page-visibility-change", this.onPageVisibilityChange.bind(this));
    }

    onLogin() {
        this.reset();
    }

    onLogout() {
        this.reset();
        this.disconnect();
    }

    onTokenChange(token) {
        if (token && this.connected) {
            this.logger("Got new token, init reconnect...");
            this.reset();
            this.disconnect();
            this.connect();
        }
    }

    onPageVisibilityChange(_, isVisible) {
        console.log("[PAGE VISIBILITY] In MQTT Service: ", isVisible);
        if (isVisible) {
            this.connect();
        } else {
            this.reset();
            this.disconnect();
        }
    }

    reset() {
        this.unsubscribe();
        this.subscribedPortals.clear();
        this.activePortals.clear();
        this.subscriptions.clear();
        this.lastMessageTs.clear();
    }

    subscribeMqttPath(path) {
        if (!this.connected || !this.client) {
            return;
        }

        if (this.vrmConfig.debug) {
            this.logger(`Subscribing to path ${path}`);
        }
        try {
            this.client.subscribe(path, {
                onFailure: res => this.onFailure(res),
                onSuccess: () => {
                    this.debouncedKeepAlive(this.parseSiteIdFromPath(path), false);
                }
            });
        } catch (e) {
            this.logger(`Error:${e.message}`);
            console.error(e);
        }
    }

    subscribe() {
        if (this.portalId === null) {
            return;
        }
        // Connect to all open subscriptions
        if (!this.connected || !this.client || this.subscribedPortals.get(this.portalId)) {
            return;
        }

        // always subscribe to system for realtime indicator
        this.activeSubscriptions.add(`N/${this.portalId}/system/#`);

        const paths = [...this.activeSubscriptions.keys()];
        paths.forEach(path => {
            this.subscribeMqttPath(path);
        })
        this.subscribedPortals.set(this.portalId, true);
    }

    unsubscribe() {
        const paths = [...this.activeSubscriptions.keys()];
        paths.forEach(path => {
            if (this.connected && this.client) {
                if (this.vrmConfig.debug) {
                    this.logger(`Unsubscribing from path ${path}`);
                }
                try {
                    this.client.unsubscribe(path, {
                        onFailure: res => this.onFailure(res),
                    });
                } catch (e) {
                    this.logger(`Error:${e.message}`);
                    console.error(e);
                }
            }
        });
        this.activeSubscriptions.clear();
        this.lastValueByPaths.clear();
        this.subscribedPortals.delete(this.portalId);
    }

    disconnect() {
        this.logger("Disconnecting");
        if (!this.connected || this.disconnected) {
            // Already disconnected
            return;
        }
        this.disconnected = true;
        this.$interval.cancel(this.keepAliveTimeout);
        this.cancelRealtimeHandlers();

        if (this.client && this.connected) {
            try {
                this.client.disconnect();
            } catch (e) {
                this.logger(`Error:${e.message}`);
                console.error(e);
            } finally {
                this.client = null;
            }
        }
        this.connected = false;
    }

    connect() {
        if (!this.host) {
            this.logger("No host set to connect to");
            return;
        }
        this.connecting = true;
        if (!this.ApiService.isLoggedIn()) {
            this.logger("Not logged in, aborting connection");
            return;
        }
        if (!this.vrmConfig.enableMQTT) {
            this.logger("Disabled in config.js");
            return;
        }
        this.logger(`Connecting: ${this.host}`);
        this.disconnected = false;
        this.UserService.getUser().then(user => {
            try {
                const userId = this.ApiService.getUserId();
                this.client = new Paho.MQTT.Client(
                    this.host,
                    this.vrmConfig.mqttPort,
                    `/mqtt?idUser=${userId}`,
                    `${user.email}_${new Date().getTime()}`
                );
                this.client.onConnectionLost = response =>
                    this.onConnectionLost(response);
                this.client.onMessageArrived = message =>
                    this.onMessageArrived(message);
                const login = {
                    onSuccess: () => this.onConnect(),
                    onFailure: res => this.onConnectFailure(res),
                    useSSL: true,
                    userName: `vrmlogin_${this.vrmConfig.mqttApiEnvironment}_${user.email}`,
                    password: this.ApiService.token(),
                    cleanSession: true
                };
                this.client.connect(login);
            } catch (e) {
                this.connecting = false;
                this.logger(`Error:${e.message}`);
                console.error(e);
            }
        });
    }

    tryReconnect() {
        if (this.reconnectTriesLeft > 0) {
            this.logger(
                "Retrying to connect..." +
                this.reconnectTriesLeft +
                " attempts remaining"
            );
            // Check if the device is online, if not then we keep trying
            if (navigator.onLine) {
                this.reconnectTriesLeft--;
            }
            this.subscribedPortals.clear();
            this.connect();
        } else {
            this.logger("Reconnect limit reached, stopping reconnect");
            this.disconnect();
        }
    }

    onConnect() {
        this.logger("Connected");
        this.connected = true;
        this.connecting = false;
        this.reconnectTriesLeft = retryConnectionLimit;

        this.subscribe();

        const _sendKeepAlives = () => {
            if (
                this.lastKeepAliveTriggered &&
                this.lastKeepAliveTriggered >
                new Date().getTime() - this.keepAliveInterval * 1000
            )
                return;

            this.lastKeepAliveTriggered = new Date().getTime();

            this.logger("Sending keepalives...");
            this.activePortals.forEach((portal, portalId) => {
                if (portal.paths.length) {
                    this.sendKeepAlive(portalId, true);
                }
            });
        };

        // Keep alive
        this.$interval.cancel(this.keepAliveTimeout);
        this.keepAliveTimeout = this.$interval(
            function () {
                if (!document.hidden) {
                    _sendKeepAlives();
                }
            },
            keepAliveCheckInterval,
            0,
            false
        );

        // Immediately send a keep alive
        _sendKeepAlives();
    }

    /**
     * Subscribes cb function to a portalId
     * which will be called whenever the realtime indicator is changed
     * @param {Number} portalId
     * @param {Function} callback
     */
    observeRealtime(portalId, callback) {
        this.realtimeWatchers.set(portalId, callback);
        callback(this.getRealtimeIndicator(portalId));
    }

    getRealtimeIndicator(portalId) {
        if (!this.realtimeTimeouts.get(portalId)) {
            return REALTIME_INDICATOR.OFFLINE;
        }

        const now = Date.now()
        const lastTs = this.lastMessageTs.get(portalId);
        const diff = (now - lastTs) / 1000;
        const tries = this.realtimeTries.get(portalId);
        if (lastTs && diff <= realtimeThreshold + realtimeErrorThreshold) {
            return REALTIME_INDICATOR.REALTIME;
        }

        if (!lastTs && tries >= 2) {
            return REALTIME_INDICATOR.OFFLINE;
        }

        if ((tries == 2 || tries == 1) && lastTs) {
            return REALTIME_INDICATOR.UNCERTAIN;
        } else if (tries >= 3) {
            return REALTIME_INDICATOR.OFFLINE;
        }
        return REALTIME_INDICATOR.LOADING;
    }

    initRealtimeHandler(portalId) {
        this.observeConnected(portalId);
        if (!this.realtimeTimeouts.has(portalId)) {
            this.checkRealtime(portalId);
            this.startRealtimeHandler(portalId);
        }
    }

    startRealtimeHandler(portalId) {
        this.$interval.cancel(this.realtimeTimeouts.get(portalId));
        this.realtimeTries.set(portalId, 0);
        this.realtimeTimeouts.set(
            portalId,
            this.$interval(
                () => {
                    this.checkRealtime(portalId);
                    const current = this.realtimeTries.get(portalId);
                    this.realtimeTries.set(portalId, current + 1);
                    const cb = this.realtimeWatchers.get(portalId);
                    if (typeof cb == 'function') {
                        cb(this.getRealtimeIndicator(portalId));
                    }
                },
                realtimeCheckInterval,
                0,
                false
            )
        );

        const cb = this.realtimeWatchers.get(portalId);
        if (typeof cb == 'function') {
            cb(this.getRealtimeIndicator(portalId));
        }
    }

    /**
     * R/<portalId>/system/0/Connected path is used in order to
     * check if installation is able to receive non-retained messages
     * @param {*} portalId
     */
    observeConnected(portalId) {
        this.subscribeToPath({
            identifier: portalId,
            dbusServiceType: "system",
            dbusPath: "/Connected",
            instance: "0",
            callback: () => { },
            callbackIdentifier: `realtime_handler_${portalId}`,
        });
    }

    dismissConnected(portalId) {
        this.unsubScribeFromPath({
            identifier: portalId,
            dbusServiceType: "system",
            dbusPath: "/Connected",
            instance: "0",
            callback: () => { },
            callbackIdentifier: `realtime_handler_${portalId}`,
        })
    }

    checkRealtime(identifier) {
        if (this.vrmConfig.debug) {
            this.logger(`Sending realtime keepalive for ${identifier}`);
        }
        this.logger(`Sending realtime keepalive for ${identifier}`);
        const message = new Paho.MQTT.Message('');
        message.destinationName = `R/${identifier}/system/0/Connected`;
        try {
            if (this.client && this.connected) {
                this.client.send(message);
            }
        } catch (e) {
            this.logger(`Error:${e.message}`);
            console.error(e);
        }
    }

    cancelRealtimeHandlers() {
        this.realtimeTimeouts.forEach((timeout, key) => {
            this.$interval.cancel(timeout);
            const cb = this.realtimeWatchers.get(key);
            if (typeof cb == 'function') {
                cb(REALTIME_INDICATOR.OFFLINE);
            }
        });
        this.realtimeTimeouts.clear();
    }

    cancelRealtimeHandler(portalId) {
        this.dismissConnected(portalId);
        const timeout = this.realtimeTimeouts.get(portalId);
        const cb = this.realtimeWatchers.get(portalId);
        if (typeof cb == 'function') {
            cb(REALTIME_INDICATOR.OFFLINE);
        }
        this.$interval.cancel(timeout);
        this.realtimeTries.delete(portalId);
        this.realtimeTimeouts.delete(portalId);
    }

    sendKeepAlive(identifier, suppressed) {
        if (this.vrmConfig.debug) {
            this.logger(`Sending keepalive ${suppressed ? 'suppressed' : 'general'} for ${identifier}`);
        }
        const message = new Paho.MQTT.Message(suppressed ? JSON.stringify({ "keepalive-options": ["suppress-republish"] }) : '');
        message.destinationName = `R/${identifier}/system/0/Serial`;
        try {
            if (this.client && this.connected && this.client.isConnected()) {
                this.client.send(message);
            }
        } catch (e) {
            this.logger(`Error:${e.message}`);
            console.error(e);
        }
    }

    onFailure(res) {
        this.logger(`Subscribe/unsubsribe failure: ${res.errorCode}`);
    }

    onConnectFailure(res) {
        this.connected = false;
        this.connecting = false;
        this.client = null;
        if (this.disconnected) return;
        this.logger("Connection failed");
        this.$log.error(res);
        setTimeout(() => this.tryReconnect(), 1000);
    }

    onConnectionLost(responseObject) {
        this.connected = false;
        if (this.disconnected) return;
        this.logger("Connection lost");
        if (responseObject.errorCode !== 0) {
            this.logger(
                `MQT :: onConnectionLost:${responseObject.errorMessage}`
            );
        }
        // Cancel keep alive
        this.$interval.cancel(this.keepAliveTimeout);
        this.cancelRealtimeHandlers();
        this.tryReconnect();
    }

    onMessageArrived(message) {
        if (message.payloadString === "") return;
        try {
            message.payload = JSON.parse(message.payloadString);
        } catch (err) {
            this.logger(
                `Error decoding payload: ${err.message}, payload: ${message.payloadString}`
            );
            return;
        }

        const siteId = this.parseSiteIdFromPath(message.destinationName);
        if (!message.retained) {
            this.lastMessageTs.set(siteId, Date.now());
            this.startRealtimeHandlerThrottled(siteId);
        }

        this.lastValueByPaths.set(message.destinationName, message);
        const subscriptions = this.subscriptions.get(message.destinationName);
        if (subscriptions) {
            subscriptions.forEach(pathSub => pathSub.callback && pathSub.callback(message));
        }

        const wildcardSubscriptionsKeys = [...this.subscriptions.keys()].filter(
            key => {
                if (!key.includes("/+/")) return false;
                return topicMatchesWildcardPath(message.destinationName, key);
            }
        );

        wildcardSubscriptionsKeys.forEach(wildcardKey => {
            const subscriptions = this.subscriptions.get(wildcardKey);
            if (subscriptions) {
                subscriptions.forEach(pathSub => pathSub.callback(message));
            }
        });
    }



    getSubscriptionFromPath(path) {
        if (path.includes("History")) {
            return path;
        }
        // We only subscribe to the first 5 levels of the path
        return path.split("/").splice(0, 5).join("/") + "/#";
    }

    subscribeToRawPath(path, callback, identifier) {
        if (
            !this.connected &&
            !this.connecting &&
            this.reconnectTriesLeft > 0
        ) {
            this.connect();
        }
        if (!this.subscriptions.has(path)) {
            this.subscriptions.set(path, []);
        }
        const mqttSubscription = this.getSubscriptionFromPath(path);
        if (!this.activeSubscriptions.has(mqttSubscription)) {
            this.activeSubscriptions.add(mqttSubscription);
            this.subscribeMqttPath(mqttSubscription);
        }


        // wildcard path, parse all last messages and see which one matches
        // we use wildcards only for gathering instances for device controls, in case we extend our usage we will have to use a trie for parsing
        if (path.includes("/+/")) {
            const lastMessages = [...this.lastValueByPaths.values()];
            const matchingMessages = lastMessages.filter(message => {
                return topicMatchesWildcardPath(message.destinationName, path);
            });

            matchingMessages.forEach(message => {
                callback(message);
            });
        }

        const message = this.lastValueByPaths.get(path);
        if (message) {
            callback(message);
        }

        const subList = this.subscriptions.get(path);
        const index = subList.findIndex(
            pathSub => pathSub.identifier === identifier
        );
        if (index >= 0) {
            // This path already has a subscription for the given identifier, so we can replace the callback
            const existing = subList[index];
            existing.callback = callback;
        } else {
            subList.push(new PathSubscription(identifier, callback));
        }
    }

    // Public service interface

    /**
     * Convert MQTT value to VRM value
     * @see vrmlogger/conversions.py
     * @param attributeCode
     * @param value
     * @returns {number|*}
     */
    convertValue(attributeCode, value) {
        if (attributeCode === "H9") return value / 86400;
        if (
            (attributeCode === "bt" && value > 0) ||
            (attributeCode === "TTG" && value > 0)
        )
            return value / 3600;

        const user = this.UserService.user;
        if (user) {
            // Add attributes of temperatures and volumes for conversion
            if (user.useFahrenheit === true) {
                if (
                    attributeCode === "BT" ||
                    attributeCode === "bT" ||
                    attributeCode === "CT" ||
                    attributeCode === "Gct" ||
                    attributeCode === "Get" ||
                    attributeCode === "Gwt" ||
                    attributeCode === "McT" ||
                    attributeCode === "ScT" ||
                    attributeCode === "T1" ||
                    attributeCode === "c0T" ||
                    attributeCode === "coT" ||
                    attributeCode === "fcab1" ||
                    attributeCode === "fcab2" ||
                    attributeCode === "fcrt1" ||
                    attributeCode === "fcrt2" ||
                    attributeCode === "fcst1" ||
                    attributeCode === "fcst2" ||
                    attributeCode === "fcst3" ||
                    attributeCode === "fcst4" ||
                    attributeCode === "glT" ||
                    attributeCode === "mcT" ||
                    attributeCode === "msC" ||
                    attributeCode === "msT" ||
                    attributeCode === "mt" ||
                    attributeCode === "tsT"
                ) {
                    return value * (9 / 5) + 32;
                }
            }
            if (user.useGallons === true) {
                if (attributeCode === "tr") {
                    return value * 0.26417205236;
                }
            }
        }
        return value;
    }

    setHost(host) {
        if (host !== this.host) {
            this.reset();
            this.disconnect();
            this.host = host;
        }
        this.lastKeepAliveTriggered = null;
    }

    setPortalId(portalId) {
        this.portalId = portalId;
    }

    /**
     * Parses N/<siteid>/subpath and returns <siteid>
     * @param path
     * @returns {*}
     */
    parseSiteIdFromPath(path) {
        path = path.split("/");
        if (path.length > 1 && path[1] !== "#" && path[1] !== "+") {
            return path[1];
        } else {
            return null;
        }
    }

    /**
     * Parses N/<siteid>/<systemType>/<instance>/subpath and returns <instance>
     * @param path
     * @returns {*}
     */
    parseInstanceFromPath(path) {
        path = path.split("/");
        if (path.length > 3 && path[3] !== "#" && path[3] !== "+") {
            return path[3];
        } else {
            return null;
        }
    }

    /**
     * Subscribe to a MQTT path
     * @param subscription object:
     *  {identifier: <siteIdentifier>,dbusServiceType: <dbusServiceType>, dbusPath: <dbusPath>,instance: <instance>,callback: <callback>,callbackIdentifier: <callbackIdentifier>}
     */
    subscribeToPath(subscription) {
        try {
            _validateSubscriptionObject(subscription, true);
        } catch (e) {
            this.logger(`Error:${e.message}`);
            console.error(e);
            return;
        }
        const path = getPath(
            subscription.identifier,
            subscription.dbusServiceType,
            subscription.dbusPath,
            subscription.instance,
            subscription.prefix
        );

        const portal =
            this.activePortals.get(subscription.identifier) ||
            this.createPortal(subscription.identifier);
        portal.addPath(path);

        this.subscribeToRawPath(
            path,
            subscription.callback,
            subscription.callbackIdentifier
        );
    }

    publishToPath(subscription, payload) {
        try {
            _validateSubscriptionObject(subscription, false);
        } catch (e) {
            this.logger(`Error:${e.message}`);
            console.error(e);
            return;
        }
        const path = getPath(
            subscription.identifier,
            subscription.dbusServiceType,
            subscription.dbusPath,
            subscription.instance,
            subscription.prefix
        );

        if (
            !this.connected &&
            !this.connecting &&
            this.reconnectTriesLeft > 0
        ) {
            this.connect();
        }

        const message = new Paho.MQTT.Message(JSON.stringify(payload));
        message.destinationName = path;
        this.logger("Publishing to path " + path);
        this.client.send(message);
    }

    createPortal(identifier) {
        const portal = new InstallationSubscription(identifier);
        this.activePortals.set(identifier, portal);
        return portal;
    }

    /**
     * Unsubscribe from a MQTT path
     * @param subscription object:
     *  {identifier: <siteIdentifier>,dbusServiceType: <dbusServiceType>, dbusPath: <dbusPath>,instance: <instance>,callbackIdentifier: <callbackIdentifier>}
     */
    unsubScribeFromPath(subscription) {
        try {
            _validateSubscriptionObject(subscription);
        } catch (e) {
            this.logger(`Error:${e.message}`);
            console.error(e);
            return;
        }
        const path = getPath(
            subscription.identifier,
            subscription.dbusServiceType,
            subscription.dbusPath,
            subscription.instance,
            subscription.prefix
        );

        const portal = this.activePortals.get(subscription.identifier);
        if (portal) {
            portal.removePath(path);
            if (!portal.paths.length) {
                this.activePortals.delete(subscription.identifier);
            }
        }

        if (!this.subscriptions.has(path)) {
            return;
        }

        // Loop through the subscriptions and delete all identified callbacks
        const subList = this.subscriptions.get(path);
        const index = subList.findIndex(
            pathSub => pathSub.identifier === subscription.callbackIdentifier
        );
        if (index >= 0) {
            subList.splice(index, 1);
        }
        if (!subList.length) {
            this.subscriptions.delete(path);
        }
    }
}

function getPath(portalId, dbusServiceType, dbusPath, instance, prefix) {
    if (prefix === undefined) {
        prefix = 'N/';
    }
    if (instance !== '' && dbusPath !== '') {
        return prefix + portalId + '/' + dbusServiceType + '/' + instance + dbusPath;
    } else {
        return prefix + portalId + '/' + dbusServiceType;
    }
}

function _validateSubscriptionObject(object, needsCallback = false) {
    if (typeof object === 'undefined' || !angular.isObject(object)) {
        throw 'No subscription object given';
    }
    const required = ['identifier', 'dbusServiceType', 'dbusPath', 'instance', 'callbackIdentifier'];
    if (needsCallback) {
        required.push('callback');
    }
    _.each(required, function (property) {
        if (typeof object[property] === 'undefined') {
            throw `No ${property} property set on subscription object`;
        }
    });
}

angular.module('vrm.core').service('MQTTService', MQTTService);
angular.module('vrm.core').service('MQTTRealtimeService', MQTTService).constant('REALTIME_INDICATOR', REALTIME_INDICATOR);
